import React, { useEffect, useState } from "react";
import { FaTimes, FaCheck, FaExclamationTriangle } from "react-icons/fa";
import ChatTab from "./ChatTab";
import MinuteTab from "./MinuteTab";
import PollsTab from "./PollsTab";
import AgendaTab from "./AgendaTab";
import './styles/tabs.css';
import './styles/DraftCard.css';
import { createPanelParticipant, getPanelbyParticipant, getPanelsById, updatePanelMeeting } from "app/apis/panels";
import { CIVIL_CASE_DIRECTOR_ROLE, CIVIL_CASE_TEAMLEADER_ROLE, WOREDA_ATTORNEY_OFFICE_HEAD, ZONAL_ATTORNEY_OFFICE_HEAD } from "app/utils/const";
import { useAppContext } from "app/contexts/AppContext";
import { useTranslation } from "react-i18next";

const MainChat = ({ change }) => {
    const { selectedPanel, setSelectedPanel } = useAppContext();
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("");
    const [meeting, setMeeting] = useState(selectedPanel);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [action, setAction] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [decline_reason, setDeclineReason] = useState("");
    const current_user_role = sessionStorage.getItem('roles') || "";
    const employee_id = sessionStorage.getItem('employee_id');
    const isDirectorOrHead =
        current_user_role.includes(CIVIL_CASE_DIRECTOR_ROLE) ||
        current_user_role.includes(ZONAL_ATTORNEY_OFFICE_HEAD) ||
        current_user_role.includes(WOREDA_ATTORNEY_OFFICE_HEAD);
    const isTeamLeader = current_user_role.includes(CIVIL_CASE_TEAMLEADER_ROLE);

    // Keep selectedPanel up-to-date with latest data from API
    useEffect(() => {
        if (!selectedPanel) return;
        const fetchPanel = async () => {
            try {
                const response = await getPanelsById(selectedPanel.uu_id);
                if (response?.data) {
                    setSelectedPanel(response.data);
                }
            } catch (err) {
                console.error(err);
            }
        };
        fetchPanel();
        // eslint-disable-next-line
    }, [selectedPanel?.uu_id]);

    // USE EFFECTS
    useEffect(() => {
        if (selectedPanel) {
            setMeeting(selectedPanel);
            if (selectedPanel.is_approved) {
                setActiveTab("agenda");
            } else {
                setActiveTab("");
            }
        } else {
            setMeeting(null);
            setActiveTab("");
        }

    }, [selectedPanel, change]);

    if (!selectedPanel) {
        return null;
    }

    // HANDLE CLOSE MEETING
    const handleCloseMeeting = async () => {
        setIsLoading(true);
        try {
            const updatedMeeting = {
                ...selectedPanel,
                closed: true,
                closed_at: new Date().toISOString(),
                closing_reason: t("Meeting Officially Closed"),
                closed_by: employee_id
            };

            await updatePanelMeeting(selectedPanel?.uu_id, updatedMeeting);

            setSelectedPanel(updatedMeeting);
            setMeeting(updatedMeeting);
            setSuccessMessage(t("Meeting successfully closed!"));
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 3000);

        } catch (err) {
            console.error("Error closing meeting:", err);
            setError(t("Failed to close the meeting. Please try again."));
        } finally {
            setIsLoading(false);
            setShowConfirmation(false);
        }
    };

    // HANDLE ACTION (APPROVE OR DECLINE)
    const handleAction = async () => {
        setIsLoading(true);
        try {
            const updatedMeeting = {
                ...selectedPanel,
                is_approved: action === "APPROVE",
                is_decline: action === "DECLINE",
                approved_on: action === "APPROVE" ? new Date().toISOString() : null,
                approved_by: action === "APPROVE" ? employee_id : null,
                declined_on: action === "DECLINE" ? new Date().toISOString() : null,
                declined_by: action === "DECLINE" ? employee_id : null,
                declined_reason: action === "DECLINE" ? decline_reason : null
            };

            await updatePanelMeeting(selectedPanel?.uu_id, updatedMeeting);
            await createPanelParticipant({
                panel: selectedPanel?.uu_id,
                participant: employee_id,
                additional_notes: action === "APPROVE" ? t("Approved") : `${t("Declined")}: ${decline_reason}`
            });
            setSelectedPanel(updatedMeeting);
            setMeeting(updatedMeeting);
            setSuccessMessage(action === "APPROVE"
                ? t("Meeting successfully approved!")
                : t("Meeting has been declined."));
            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                setShowConfirmation(false);
                setDeclineReason("");
            }, 3000);
        } catch (err) {
            console.error("Error updating meeting:", err);
            setError(t("Failed to update the meeting. Please try again."));
        } finally {
            setIsLoading(false);
            setAction(null);
            setShowConfirmation(false);
        }
    };

    // HANDLE CONFIRMATION
    const handleConfirmation = (actionType) => {
        setAction(actionType);
        setShowConfirmation(true);
    };

    const handleCancel = () => {
        setShowConfirmation(false);
        setAction(null);
        setDeclineReason("");
    };

    // RENDERING FUNCTIONS
    const renderConfirmationDialog = () => {
        let title, message, confirmText, confirmClass, extraContent;
        if (action === "APPROVE") {
            title = t("Approve Meeting");
            message = t("Are you sure you want to approve this meeting?");
            confirmText = t("Approve");
            confirmClass = "bg-green-500 hover:bg-green-600";
        } else if (action === "DECLINE") {
            title = t("Decline Meeting");
            message = t("Are you sure you want to decline this meeting? Please provide a reason.");
            confirmText = t("Decline");
            confirmClass = "bg-red-500 hover:bg-red-600";
            extraContent = (
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("Decline Reason")}
                    </label>
                    <textarea
                        className="w-full p-2 border border-gray-300 rounded"
                        rows={3}
                        value={decline_reason}
                        onChange={(e) => setDeclineReason(e.target.value)}
                        placeholder={t("Enter reason for declining...")}
                        required
                    />
                </div>
            );
        } else {
            title = t("Close Meeting");
            message = t("Are you sure you want to close the meeting?");
            confirmText = t("Close");
            confirmClass = "bg-red-500 hover:bg-red-600";
        }

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000]">
                <div className="bg-white p-6 rounded-lg shadow-lg w-96">
                    <h3 className="text-xl font-semibold mb-4">{title}</h3>
                    <p className="mb-4">{message}</p>
                    {extraContent}
                    <div className="flex justify-end gap-4">
                        <button
                            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                            onClick={handleCancel}
                        >
                            {t("Cancel")}
                        </button>
                        <button
                            className={`px-4 py-2 text-white rounded ${confirmClass}`}
                            onClick={action ? handleAction : handleCloseMeeting}
                            disabled={isLoading || (action === "DECLINE" && !decline_reason)}
                        >
                            {isLoading ? t("Processing...") : confirmText}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // RENDERING SUCCESS MESSAGE
    const renderSuccessMessage = () => {
        return (
            <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded shadow-lg flex items-center z-[2000]">
                <FaCheck className="mr-2" />
                <span>{successMessage}</span>
            </div>
        );
    };

    return (
        <div className="flex-1 flex flex-col relative bg-white w-[50vw]">
            {/* Upper section */}
            {isDirectorOrHead && selectedPanel?.is_approved && (
                <div className="p-4 bg-white flex justify-between border-b">
                    <h2 className="text-[22] font-semibold">&nbsp;</h2>
                    <div className="flex gap-2">
                        <button
                            className="p-2 bg-red-500 text-white rounded flex items-center gap-2"
                            onClick={() => handleConfirmation(null)}
                            disabled={isLoading || selectedPanel?.closed}
                        >
                            {selectedPanel?.closed ? (
                                <>{t("Meeting is Closed")}</>
                            ) : (
                                <><FaTimes /> {t("Close Meeting")}</>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            {showConfirmation && renderConfirmationDialog()}

            {/* Success/Error Messages */}
            {showSuccess && renderSuccessMessage()}
            {error && (
                <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg flex items-center z-[2000]">
                    <FaExclamationTriangle className="mr-2" />
                    <span>{error}</span>
                </div>
            )}

            {selectedPanel?.is_approved ? (
                <div className="flex bg-white p-2 border-b">
                    <button
                        className={`flex-1 text-center p-2 ${activeTab === "agenda" ? "text-black font-semibold border-b-2 border-black" : "text-gray-500 hover:text-black"}`}
                        onClick={() => setActiveTab("agenda")}
                    >
                        <img src={`${process.env.PUBLIC_URL}/assets/agenda.svg`} className="inline-block mr-2" alt={t("Agenda")} />
                        {t('Agenda')}
                    </button>
                    <button
                        className={`flex-1 text-center p-2 ${activeTab === "chat" ? "text-black font-semibold border-b-2 border-black" : "text-gray-500 hover:text-black"}`}
                        onClick={() => setActiveTab("chat")}
                    >
                        <img src={`${process.env.PUBLIC_URL}/assets/chats.svg`} className="inline-block mr-2" alt={t("Chat")} />
                        {t('Chat')}
                    </button>
                    <button
                        className={`flex-1 text-center p-2 ${activeTab === "polls" ? "text-black font-semibold border-b-2 border-black" : "text-gray-500 hover:text-black"}`}
                        onClick={() => setActiveTab("polls")}
                    >
                        <img src={`${process.env.PUBLIC_URL}/assets/polls.svg`} className="inline-block mr-2" alt={t("Polls")} />
                        {t('Polls')}
                    </button>
                    <button
                        className={`flex-1 text-center p-2 ${activeTab === "minute" ? "text-black font-semibold border-b-2 border-black" : "text-gray-500 hover:text-black"}`}
                        onClick={() => setActiveTab("minute")}
                    >
                        <img src={`${process.env.PUBLIC_URL}/assets/minute.svg`} className="inline-block mr-2" alt={t("Minute")} />
                        {t('Minute')}
                    </button>
                </div>
            ) : (
                <div className="">
                    {isDirectorOrHead ? (
                        <div className="flex flex-col items-center justify-center h-full p-4">
                            <div className="bg-white shadow-lg rounded-lg p-6 w-full ">
                                <h2 className="text-xl font-semibold mb-4">{selectedPanel?.panel_title || selectedPanel?.title}</h2>
                                <p className="text-gray-700 mb-4">{selectedPanel?.agenda}</p>
                                <div className="flex justify-center gap-2">
                                    {selectedPanel?.is_decline ? (
                                        <div className="w-full p-4 bg-red-100 rounded-lg">
                                            <div className="text-red-700 font-semibold">
                                                {t('Declined')}
                                            </div>
                                            {selectedPanel.declined_reason && (
                                                <div className="mt-2 text-red-600">
                                                    {t('Reason')}: {selectedPanel.declined_reason}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <button
                                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                                                onClick={() => handleConfirmation("DECLINE")}
                                                disabled={isLoading}
                                            >
                                                {t("Decline Meeting")}
                                            </button>
                                            <button
                                                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                                                onClick={() => handleConfirmation("APPROVE")}
                                                disabled={isLoading}
                                            >
                                                {t("Approve Meeting")}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full p-4">
                            <div className="bg-white shadow-lg rounded-lg p-6 w-full ">
                                <h2 className="text-xl font-semibold mb-4">{selectedPanel?.panel_title || selectedPanel?.title}</h2>
                                <p className="text-gray-700 mb-4">{selectedPanel?.agenda}</p>
                                <div className="flex justify-center gap-2">
                                    {selectedPanel?.is_decline ? (
                                        <div className="w-full p-4 bg-red-100 rounded-lg">
                                            <div className="text-red-700 font-semibold">{t('Declined')}</div>
                                            {selectedPanel.declined_reason && (
                                                <div className="mt-2 text-red-600">
                                                    {t('Reason')}: {selectedPanel.declined_reason}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <button
                                            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                                            disabled
                                        >
                                            {t("PENDING APPROVAL")}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Render active tab component */}
            {activeTab === "chat" && <ChatTab isDirectorOrHead={isDirectorOrHead} />}
            {activeTab === "minute" && <MinuteTab isDirectorOrHead={isDirectorOrHead} />}
            {activeTab === "polls" && <PollsTab isDirectorOrHead={isDirectorOrHead} />}
            {activeTab === "agenda" && <AgendaTab isDirectorOrHead={isDirectorOrHead} />}
        </div>
    );
};

export default MainChat;